import { useState, useEffect } from 'react'
import { X, Globe, User, Loader, ExternalLink, Copy, Check, Camera, MessageSquare, Sparkles } from 'lucide-react'
import { useStore } from '../store/useStore'
import { publishToGallery, slugify } from '../services/gallerySync'
import { showToast } from './ui/Toast'

export default function PublishModal({ onClose }) {
  const { sketchName, isAuthenticated, p5Credentials, aiMessages } = useStore()
  
  const [title, setTitle] = useState(sketchName)
  const [slug, setSlug] = useState(slugify(sketchName))
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [thumbnail, setThumbnail] = useState(null)
  const [publishAs, setPublishAs] = useState('public') // 'public' or 'user'
  const [publishing, setPublishing] = useState(false)
  const [result, setResult] = useState(null)
  const [copied, setCopied] = useState(false)
  const [includeAiChat, setIncludeAiChat] = useState(true) // Default to including AI chat
  const [generatingDescription, setGeneratingDescription] = useState(false)

  // Get username if authenticated
  const username = isAuthenticated && p5Credentials?.username
    ? `${p5Credentials.username}-p5js`
    : null

  // Update slug when title changes
  useEffect(() => {
    setSlug(slugify(title))
  }, [title])

  // Capture thumbnail from canvas
  const handleCaptureThumbnail = () => {
    try {
      // Find the p5 canvas in the preview iframe or main canvas
      const canvas = document.querySelector('#preview-frame canvas') || 
                     document.querySelector('canvas')
      
      if (!canvas) {
        showToast('No canvas found to capture', 'error')
        return
      }
      
      // Try to capture - will fail if canvas is tainted (cross-origin images)
      const dataUrl = canvas.toDataURL('image/png')
      setThumbnail(dataUrl)
      showToast('Thumbnail captured!', 'success')
    } catch (error) {
      console.log('Thumbnail capture failed:', error)
      showToast('Could not capture thumbnail (sketch may use external images)', 'error')
    }
  }

  const handlePublish = async () => {
    if (!title.trim() || !slug.trim()) {
      showToast('Please enter a title', 'error')
      return
    }
    
    setPublishing(true)
    try {
      const data = await publishToGallery({
        title: title.trim(),
        slug: slug.trim(),
        description: description.trim(),
        tags: tags.trim(),
        thumbnail,
        publishAs,
        aiChat: includeAiChat && aiMessages.length > 0 ? aiMessages : null
      })
      
      setResult(data)
      showToast('Published successfully!', 'success')
      
    } catch (error) {
      showToast(error.message || 'Publish failed', 'error')
    } finally {
      setPublishing(false)
    }
  }

  const handleCopy = () => {
    if (result?.galleryUrl) {
      navigator.clipboard.writeText(result.galleryUrl)
      setCopied(true)
      showToast('URL copied!', 'success')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  // Generate description using AI
  const handleGenerateDescription = async () => {
    const { files } = useStore.getState()
    const sketchCode = files.find(f => f.name === 'sketch.js')?.content || ''
    
    if (!sketchCode.trim()) {
      showToast('No sketch code to analyze', 'error')
      return
    }
    
    setGeneratingDescription(true)
    try {
      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'openai',
          model: 'gpt-5.1',
          reasoningMode: 'none',
          messages: [{
            role: 'user',
            content: `Write a brief, engaging 1-2 sentence description for this p5.js sketch. Focus on what it creates visually and any interactivity. Be concise and friendly. Do not use markdown or quotes, just plain text.\n\nSketch code:\n${sketchCode.slice(0, 3000)}`
          }]
          // Note: Not sending deviceId so no job record is created for this quick request
        })
      })
      
      if (!response.ok) throw new Error('Failed to generate')
      
      // Read the streaming response
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let result = ''
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        
        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === 'delta' && data.text) {
                result += data.text
              } else if (data.type === 'done' && data.content) {
                result = data.content
              }
            } catch (e) {}
          }
        }
      }
      
      if (result) {
        setDescription(result.trim().slice(0, 500))
        showToast('Description generated!', 'success')
      }
    } catch (error) {
      console.error('Generate description error:', error)
      showToast('Failed to generate description', 'error')
    } finally {
      setGeneratingDescription(false)
    }
  }

  const previewUrl = publishAs === 'user' && username
    ? `p5js.ai/gallery/u/${username}/${slug || 'sketch'}`
    : `p5js.ai/gallery/s/${slug || 'sketch'}`

  // Success state
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
        <div className="bg-ide-surface border border-ide-border rounded-xl max-w-md w-full p-6 shadow-modal animate-scale-in">
          <div className="text-center">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-xl font-semibold text-ide-text mb-2">Published!</h2>
            <p className="text-ide-textMuted mb-6">Your sketch is now live</p>
            
            <div className="bg-ide-bg rounded-lg p-4 mb-6 border border-ide-border">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={result.galleryUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm text-ide-accent truncate outline-none"
                />
                <button 
                  onClick={handleCopy} 
                  className="p-2 hover:bg-ide-surfaceHover rounded transition-colors"
                  title="Copy URL"
                  aria-label="Copy URL"
                >
                  {copied ? (
                    <Check size={18} className="text-ide-success" />
                  ) : (
                    <Copy size={18} className="text-ide-textMuted" />
                  )}
                </button>
              </div>
            </div>
            
            <div className="flex gap-3">
              <a
                href={result.galleryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-ide-accent hover:bg-ide-accentHover text-white py-2.5 px-4 rounded-lg transition-colors"
              >
                <ExternalLink size={18} />
                View Sketch
              </a>
              <button
                onClick={onClose}
                className="flex-1 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border text-ide-text py-2.5 px-4 rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-ide-surface border border-ide-border rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-modal animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-ide-border sticky top-0 bg-ide-surface">
          <h2 className="text-lg font-semibold text-ide-text flex items-center gap-2">
            <Globe size={18} className="text-ide-success" />
            Publish to Gallery
          </h2>
          <button 
            onClick={onClose} 
            className="text-ide-textMuted hover:text-ide-text transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Form */}
        <div className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-ide-bg border border-ide-border rounded-lg px-3 py-2.5 text-sm text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
              placeholder="My Awesome Sketch"
              maxLength={100}
            />
          </div>
          
          {/* Slug */}
          <div>
            <label className="block text-sm font-medium mb-1.5">URL Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(slugify(e.target.value))}
              className="w-full bg-ide-bg border border-ide-border rounded-lg px-3 py-2.5 text-sm text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30 font-mono"
              placeholder="my-awesome-sketch"
              maxLength={60}
            />
            <p className="text-xs text-ide-textDim mt-1.5 truncate">
              {previewUrl}
            </p>
          </div>
          
          {/* Publish As */}
          <div>
            <label className="block text-sm font-medium mb-2">Publish as</label>
            <div className="space-y-2">
              {/* Public Gallery Option */}
              <label className="flex items-center gap-3 p-3 bg-ide-bg rounded-lg cursor-pointer border border-ide-border hover:border-ide-borderHover transition-colors">
                <input
                  type="radio"
                  name="publishAs"
                  value="public"
                  checked={publishAs === 'public'}
                  onChange={() => setPublishAs('public')}
                  className="w-4 h-4 text-ide-accent bg-ide-surface border-ide-border focus:ring-ide-accent focus:ring-2"
                />
                <Globe size={18} className="text-ide-textMuted" />
                <div className="flex-1">
                  <div className="font-medium text-sm text-ide-text">Public Gallery</div>
                  <div className="text-xs text-ide-textDim">Anyone can find it</div>
                </div>
              </label>
              
              {/* User Gallery Option */}
              <label className={`flex items-center gap-3 p-3 bg-ide-bg rounded-lg border border-ide-border transition-colors ${
                username ? 'cursor-pointer hover:border-ide-borderHover' : 'opacity-50 cursor-not-allowed'
              }`}>
                <input
                  type="radio"
                  name="publishAs"
                  value="user"
                  checked={publishAs === 'user'}
                  onChange={() => username && setPublishAs('user')}
                  disabled={!username}
                  className="w-4 h-4 text-ide-accent bg-ide-surface border-ide-border focus:ring-ide-accent focus:ring-2"
                />
                <User size={18} className="text-ide-textMuted" />
                <div className="flex-1">
                  <div className="font-medium text-sm text-ide-text">My Gallery</div>
                  <div className="text-xs text-ide-textDim">
                    {username 
                      ? `As ${p5Credentials?.username}` 
                      : 'Login with p5js.org to enable'}
                  </div>
                </div>
              </label>
            </div>
          </div>
          
          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium">
                Description <span className="text-gray-500">(optional)</span>
              </label>
              <button
                type="button"
                onClick={handleGenerateDescription}
                disabled={generatingDescription}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-ide-accent hover:text-ide-accentHover bg-ide-accent/10 hover:bg-ide-accent/20 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Generate description with AI"
              >
                {generatingDescription ? (
                  <>
                    <Loader size={12} className="animate-spin" />
                    Writing...
                  </>
                ) : (
                  <>
                    <Sparkles size={12} />
                    AI Write
                  </>
                )}
              </button>
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-ide-bg border border-ide-border rounded-lg px-3 py-2.5 text-sm text-ide-text placeholder-ide-textDim resize-none focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
              placeholder="A short description of your sketch..."
              maxLength={500}
            />
            <p className="text-xs text-ide-textDim mt-1 text-right">
              {description.length}/500
            </p>
          </div>
          
          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Tags <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full bg-ide-bg border border-ide-border rounded-lg px-3 py-2.5 text-sm text-ide-text placeholder-ide-textDim focus:outline-none focus:border-ide-borderFocus focus:ring-1 focus:ring-ide-accent/30"
              placeholder="#itp #generative #research"
              maxLength={100}
            />
            <p className="text-xs text-ide-textDim mt-1">
              Separate with spaces or commas
            </p>
          </div>
          
          {/* Thumbnail */}
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Preview Thumbnail <span className="text-gray-500">(optional)</span>
            </label>
            <div className="flex gap-3 items-start">
              {thumbnail ? (
                <div className="relative">
                  <img 
                    src={thumbnail} 
                    alt="Thumbnail preview" 
                    className="w-24 h-24 object-cover rounded-lg border border-ide-border"
                  />
                  <button
                    onClick={() => setThumbnail(null)}
                    className="absolute -top-2 -right-2 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border rounded-full p-1"
                    title="Remove thumbnail"
                    aria-label="Remove thumbnail"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 bg-ide-bg border border-ide-border border-dashed rounded-lg flex items-center justify-center">
                  <Camera size={24} className="text-ide-textDim" />
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={handleCaptureThumbnail}
                  className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border text-ide-text rounded-lg text-sm transition-colors"
                >
                  <Camera size={16} />
                  {thumbnail ? 'Recapture' : 'Capture Current Frame'}
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Run your sketch to the desired state, then capture
                </p>
              </div>
            </div>
          </div>
          
          {/* AI Chat Option */}
          {aiMessages.length > 0 && (
            <div className="pt-2 border-t border-ide-border">
              <label className="flex items-start gap-3 p-3 bg-ide-bg rounded-lg cursor-pointer border border-ide-border hover:border-ide-borderHover transition-colors">
                <input
                  type="checkbox"
                  checked={includeAiChat}
                  onChange={(e) => setIncludeAiChat(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-ide-accent bg-ide-surface border-ide-border rounded focus:ring-ide-accent focus:ring-2"
                />
                <MessageSquare size={18} className="text-ide-accent mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-sm text-ide-text">Include AI Chat History</div>
                  <div className="text-xs text-ide-textDim mt-0.5">
                    Share the {aiMessages.length} message{aiMessages.length !== 1 ? 's' : ''} that helped create this sketch
                  </div>
                </div>
              </label>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-ide-border sticky bottom-0 bg-ide-surface">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 px-4 bg-ide-surface hover:bg-ide-surfaceHover border border-ide-border text-ide-text rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing || !title.trim() || !slug.trim()}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 bg-ide-accent hover:bg-ide-accentHover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {publishing ? (
              <>
                <Loader className="animate-spin" size={18} />
                Publishing...
              </>
            ) : (
              <>
                🚀 Publish
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
